import { useState, useEffect, useCallback, useMemo } from 'react'
import { Radio, RadioGroup } from '@headlessui/react'
import { CheckIcon } from '@heroicons/react/20/solid'
import { CheckCircleIcon as OutlineCircleCheckIcon } from '@heroicons/react/24/outline'
import { ReactSVG } from 'react-svg'
import { purchaseBussinessItems } from '@/data/selectOptions'
import { PurchaseItem } from '@/types'

const defaultBuyingOptions = ['440', '400', '180', '100', '160', '110', '470', '990']
interface PurchaseItemsProps {
  onSelect: (item: string) => void
  defaultValue?: string
  completedSelectingPurchaseItem?: boolean
  buyingOptions?: {
    icons: PurchaseItem[],
    list: string[]
  }
}

const PurchaseItems: React.FC<PurchaseItemsProps> = ({ onSelect, defaultValue, completedSelectingPurchaseItem = false, buyingOptions = { icons: [], list: [] } }) => {
  const finalBuyingOptions = useMemo(() => {
    return buyingOptions.list.length > 0
      ? purchaseBussinessItems.filter(item => buyingOptions.list.includes(item.value))
      : defaultBuyingOptions
        .map(value => purchaseBussinessItems.find(item => item.value === value))
        .filter(Boolean) as PurchaseItem[]
  }, [buyingOptions.list])

  const [selectedPurchaseItem, setSelectedPurchaseItem] = useState<PurchaseItem>(finalBuyingOptions[0])

  useEffect(() => {
    if (defaultValue) {
      const defaultOption = finalBuyingOptions.find(item => item.value === defaultValue)
      if (defaultOption) {
        setSelectedPurchaseItem(defaultOption)
      }
    }
  }, [defaultValue, finalBuyingOptions])

  const handleSelect = (item: PurchaseItem) => {
    setSelectedPurchaseItem(item)
    onSelect(item.value)
  }

  const getSVGIcon = useCallback(
    (item: PurchaseItem) =>
      buyingOptions.icons.find((i) => i.value === item.value)?.icon || item.icon,
    [buyingOptions.icons])

  return (
    <>
      <fieldset>
        {!completedSelectingPurchaseItem ? (
        <RadioGroup
          value={selectedPurchaseItem}
          onChange={handleSelect}
          className='pt-1 pb-10 grid grid-cols-1 gap-y-6 sm:grid-cols-2 lg:grid-cols-4 sm:gap-x-4'
        >
          {finalBuyingOptions.map((purchaseItem) => (
            <Radio
            key={purchaseItem.value}
            value={purchaseItem}
            aria-label={purchaseItem.title}
            aria-description={`Purchase loan for ${purchaseItem.title}`}
            className='
              group select-none mx-auto relative size-44 flex flex-col cursor-pointer rounded-full bg-[var(--purchase-item-bg)]
              text-[var(--purchase-item-text)] p-4 shadow-xs focus:outline-hidden
              transition-all duration-500
              data-[checked]:bg-[var(--purchase-item-bg-hover)] data-[checked]:text-[var(--purchase-item-text-hover)]
              hover:bg-[var(--purchase-item-bg-hover)] hover:text-[var(--purchase-item-text-hover)]
              data-focus:bg-[var(--purchase-item-bg-hover)] data-focus:text-[var(--purchase-item-text-hover)]
            '
          >
            <ReactSVG className='PurchaseItems-svg h-[70px] text-[var(--theme-icon)] group-hover:text-[var(--purchase-item-text-hover)] group-data-[checked]:text-[var(--purchase-item-text-hover)] transition duration-500' src={getSVGIcon(purchaseItem)} />
            <span className='text-sm font-[var(--font-weight-bold)] h-[70px] flex items-center justify-center text-center px-4 transition duration-500'>{purchaseItem.title}</span>
            <span className='absolute right-2 top-2 items-center justify-center rounded-full size-8 border-2 border-white bg-[var(--purchase-item-bg-hover)] hidden group-data-[checked]:flex'>
              <CheckIcon aria-hidden='true' className='size-6 text-white' />
            </span>
          </Radio>
          ))}
        </RadioGroup>
        ) : (
        <div>
          <div className='h-16 bg-[var(--checkbox-active-bg)] text-white flex items-center justify-between rounded-t-sm -mt-12 -mx-4 md:-mx-8 py-2 px-4 md:px-8'>
            <ReactSVG className='PurchaseItems-svg--header h-full flex items-center' src={getSVGIcon(selectedPurchaseItem)} />
            <h4 className='grow pl-6 font-[var(--font-weight-bold)] text-xl'>{selectedPurchaseItem.title}</h4>
            <OutlineCircleCheckIcon aria-hidden='true' className='size-8' />
          </div>
        </div>
        )}
      </fieldset>
    </>
  )
}

export default PurchaseItems